import { hasUnknownEntityMetadata, validUrl } from "../markup/shared"
import { isUrlButtonStyle } from "../buttons/urlButtonStyle"
import {
  findSlotMarkers,
  groupSlotOccurrences,
  isWholeSlotMarker,
} from "../model/slots"
import type {
  MessageContent,
  ParsedMessage,
  ParsedMessageEntity,
  SlotOccurrence,
  UrlButtonStyle,
} from "../types"
import type { TelegramPreviewSlot } from "./telegramMessagePreview"

export interface PreviewAnnotation {
  entity: ParsedMessageEntity
  href?: string
  occurrence?: SlotOccurrence
  missing?: boolean
  /** Label slots inside a link are passive spans, with sibling context controls. */
  passive?: boolean
  textSlots?: { label: string; occurrence: SlotOccurrence }[]
}

export interface PreviewText {
  text: string
  annotations: PreviewAnnotation[]
}

export interface PreviewButton {
  text: string
  url?: string
  style?: UrlButtonStyle
  missing: boolean
  occurrence?: SlotOccurrence
  textSlots: { label: string; occurrence: SlotOccurrence; missing: boolean }[]
  occurrences: SlotOccurrence[]
}

/** Hide only internal markers, never arbitrary user-authored UUID strings. */
export function previewLabel(text: string, replacement = "Ссылка"): string {
  let result = "",
    cursor = 0
  for (const marker of findSlotMarkers(text)) {
    result += text.slice(cursor, marker.offset) + replacement
    cursor = marker.offset + marker.length
  }
  return result + text.slice(cursor)
}

export function safePreviewHref(
  url: string | null | undefined,
): string | undefined {
  return url && validUrl(url) && !isWholeSlotMarker(url) ? url : undefined
}

/** A one-way presentation projection. No editor range edits, serialization,
 * source enrichment or module-specific bindings are used here. */
export function resolvePreview(
  content: MessageContent,
  parsed: ParsedMessage,
  slots: readonly TelegramPreviewSlot[] = [],
): PreviewText & { buttons: PreviewButton[][]; warnings: string[] } {
  const warnings = new Set<string>()
  const markers = findSlotMarkers(parsed.text)
  const textOccurrences = markers.map((marker): SlotOccurrence => {
    const mapping = parsed.mapping.find(
      (item) =>
        item.kind === "text" &&
        item.textRange.offset <= marker.offset &&
        item.textRange.offset + item.textRange.length > marker.offset,
    )
    return {
      ...marker,
      offset: mapping
        ? mapping.sourceRange.offset + marker.offset - mapping.textRange.offset
        : marker.offset,
      document: "text",
      path: "/text",
      label: "",
    }
  })
  const linkOccurrences = new Map<number, SlotOccurrence>()
  parsed.entities.forEach((entity, index) => {
    if (entity.type !== "text_link" || !isWholeSlotMarker(entity.url)) return
    const marker = findSlotMarkers(entity.url)[0]
    const mapping = parsed.mapping.find(
      (item) =>
        item.kind === "destination" &&
        item.textRange.offset === entity.offset &&
        item.textRange.length === entity.length,
    )
    linkOccurrences.set(index, {
      ...marker,
      document: content.formatting.kind === "markup" ? "text" : "entity-url",
      path:
        content.formatting.kind === "markup"
          ? "/text"
          : `/formatting/entities/${index}/url`,
      offset: mapping?.sourceRange.offset ?? 0,
      label: previewLabel(
        parsed.text.slice(entity.offset, entity.offset + entity.length),
        "",
      ).trim(),
    })
  })
  const buttonOccurrences = content.buttons.map((row, rowIndex) =>
    row.map((button, index) => {
      const path = `/buttons/${rowIndex}/${index}`
      const label = previewLabel(button.text, "").trim()
      const text = findSlotMarkers(button.text).map(
        (marker): SlotOccurrence => ({
          ...marker,
          document: "button-text",
          path: `${path}/text`,
          label,
        }),
      )
      const url: SlotOccurrence | undefined = isWholeSlotMarker(button.url)
        ? {
            ...findSlotMarkers(button.url)[0],
            document: "button-url",
            path: `${path}/url`,
            label,
          }
        : undefined
      return { text, url }
    }),
  )
  // Markup destinations precede their label in original source. Native links
  // are ordered by their semantic position, never by the entity array order.
  const ordered = [
    ...textOccurrences.map((occurrence, index) => ({
      position:
        content.formatting.kind === "markup"
          ? occurrence.offset!
          : markers[index].offset,
      occurrence,
    })),
    ...Array.from(linkOccurrences, ([index, occurrence]) => ({
      position:
        content.formatting.kind === "markup"
          ? occurrence.offset!
          : parsed.entities[index].offset,
      occurrence,
    })),
  ]
    .sort((a, b) => a.position - b.position)
    .map((item) => item.occurrence)
  const groups = groupSlotOccurrences([
    ...ordered,
    ...buttonOccurrences
      .flat()
      .flatMap((item) => [...item.text, ...(item.url ? [item.url] : [])]),
  ])
  const descriptors = new Map(slots.map((slot) => [slot.id, slot]))
  const labels = new Map(groups.map((group) => [group.slotId, group.label]))
  const describe = (occurrence: SlotOccurrence) => {
    const descriptor = descriptors.get(occurrence.slotId)
    const label =
      previewLabel(descriptor?.label ?? "").trim() ||
      labels.get(occurrence.slotId) ||
      "Ссылка"
    const url = safePreviewHref(descriptor?.url)
    if (descriptor?.url && !url)
      warnings.add(`Некорректный URL: ${label}. Значение не показано.`)
    return { label, url }
  }
  const replacements = markers.map((marker, index) => {
    const occurrence = textOccurrences[index]
    const { label, url } = describe(occurrence)
    return { ...marker, text: url ?? label, url, occurrence }
  })
  const mapBoundary = (position: number) =>
    position +
    replacements.reduce(
      (shift, replacement) =>
        shift +
        (replacement.offset + replacement.length <= position
          ? replacement.text.length - replacement.length
          : 0),
      0,
    )
  let text = "",
    cursor = 0
  const slotAnnotations: PreviewAnnotation[] = []
  for (const replacement of replacements) {
    text += parsed.text.slice(cursor, replacement.offset)
    slotAnnotations.push({
      entity: {
        type: "text_link",
        offset: text.length,
        length: replacement.text.length,
        url: replacement.url ?? "",
      },
      href: replacement.url,
      occurrence: replacement.occurrence,
      missing: !replacement.url,
    })
    text += replacement.text
    cursor = replacement.offset + replacement.length
  }
  text += parsed.text.slice(cursor)
  const annotations = parsed.entities.flatMap(
    (entity, index): PreviewAnnotation[] => {
      if (hasUnknownEntityMetadata(entity))
        warnings.add(
          "Дополнительные metadata аннотации сохранены, но не отображаются.",
        )
      if (entity.type === "custom_emoji")
        warnings.add(
          "Пользовательский эмодзи показан исходным символом, без загрузки Telegram-медиа.",
        )
      // A native bare-URL annotation around a marker is represented by the slot,
      // not a nested second anchor. Other metadata is copied, never reconstructed.
      if (
        entity.type === "url" &&
        replacements.some(
          (item) =>
            item.offset === entity.offset && item.length === entity.length,
        )
      )
        return []
      const offset = mapBoundary(entity.offset)
      const projected = {
        ...entity,
        offset,
        length: mapBoundary(entity.offset + entity.length) - offset,
      }
      const occurrence = linkOccurrences.get(index)
      const href = occurrence
        ? describe(occurrence).url
        : entity.type === "text_link"
          ? safePreviewHref(entity.url)
          : entity.type === "url"
            ? safePreviewHref(text.slice(offset, offset + projected.length))
            : undefined
      if (
        (entity.type === "text_link" || entity.type === "url") &&
        !href &&
        !occurrence
      )
        warnings.add("Небезопасный или неподдерживаемый URL не активирован.")
      return [
        { entity: projected, href, occurrence, missing: !!occurrence && !href },
      ]
    },
  )
  for (const slot of slotAnnotations) {
    const parent = annotations.find(
      ({ entity }) =>
        (entity.type === "text_link" || entity.type === "url") &&
        entity.offset <= slot.entity.offset &&
        entity.offset + entity.length >=
          slot.entity.offset + slot.entity.length,
    )
    if (!parent || !slot.occurrence) continue
    slot.passive = true
    parent.textSlots = [
      ...(parent.textSlots ?? []),
      {
        label: describe(slot.occurrence).label,
        occurrence: slot.occurrence,
      },
    ]
  }
  const buttons = content.buttons.map((row, rowIndex) =>
    row.map((button, index): PreviewButton => {
      const occurrences = buttonOccurrences[rowIndex][index]
      let text = "",
        cursor = 0
      const textSlots = occurrences.text.map((occurrence) => {
        const { label, url } = describe(occurrence)
        text += button.text.slice(cursor, occurrence.offset) + (url ?? label)
        cursor = occurrence.offset! + occurrence.length!
        return { label, occurrence, missing: !url }
      })
      text += button.text.slice(cursor)
      const url = occurrences.url
        ? describe(occurrences.url).url
        : safePreviewHref(button.url)
      if (!url && !occurrences.url)
        warnings.add(
          "Небезопасный или неподдерживаемый URL кнопки не активирован.",
        )
      return {
        text,
        url,
        ...(isUrlButtonStyle(button.style) ? { style: button.style } : {}),
        missing: !url,
        occurrence: occurrences.url,
        textSlots,
        occurrences: [
          ...occurrences.text,
          ...(occurrences.url ? [occurrences.url] : []),
        ],
      }
    }),
  )
  return {
    text,
    annotations: [...annotations, ...slotAnnotations],
    buttons,
    warnings: [...warnings],
  }
}
